import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d59u42lcrgmf5papd630', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/8d07b83a-651c-4150-bc43-cd55f3c060c7.png" 
                alt="MOLINHAS LTDA Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>MOLINHAS LTDA</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Home</a>
              <a href="#vpn-service" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>VPN Service</a>
              <a href="#features" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Features</a>
              <a href="#contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Contact</a>
              <a href="#about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>About</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/b02d5545cc2a8e6556667b286df076eb.jpg" 
            alt="Secure Digital Network" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="mb-6">
            <div className="text-6xl md:text-7xl font-bold text-white mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>SECURE YOUR</div>
            <div className="text-6xl md:text-7xl italic text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Digital Freedom</div>
          </h1>
          <p className="text-lg text-white/85 mb-12 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Enterprise-grade VPN protection for your privacy and security
          </p>
          <a 
            href="#contact" 
            className="inline-flex items-center px-12 py-4 bg-white text-gray-900 rounded-full text-base font-semibold hover:scale-105 transition-transform duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Protected Now
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Premium VPN Service
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience unparalleled online privacy and security with our cutting-edge VPN technology. 
              Protect your digital life with military-grade encryption and lightning-fast connections.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Privacy Protection</h3>
                  <p className="text-gray-600">Your online activities remain completely private with our strict no-log policy and advanced encryption protocols.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-speed-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Lightning-Fast Speeds</h3>
                  <p className="text-gray-600">Enjoy seamless browsing, streaming, and downloading without compromising on speed or performance.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-global-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Worldwide Access</h3>
                  <p className="text-gray-600">Connect to servers across the globe and access content from anywhere without restrictions or limitations.</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20VPN%20interface%20dashboard%20showing%20secure%20connection%20with%20world%20map%20and%20encrypted%20data%20flow%2C%20clean%20minimalist%20design%20with%20blue%20and%20cyan%20accent%20colors%2C%20professional%20cybersecurity%20application%20screenshot%20with%20network%20protection%20visualization%2C%20high-tech%20security%20software%20interface&width=800&height=900&seq=vpn-service-001&orientation=portrait" 
                alt="VPN Service Interface" 
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Military-Grade<br />Security Features
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-18 h-18 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mx-auto mb-6">
                <i className="ri-lock-password-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">256-Bit Encryption</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Bank-level encryption protecting your data from any unauthorized access. Your information stays secure at all times.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-18 h-18 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mx-auto mb-6">
                <i className="ri-eye-off-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">No-Log Policy</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We never track, collect, or share your private data or browsing activity. Your privacy is our top priority.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-18 h-18 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mx-auto mb-6">
                <i className="ri-earth-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Global Servers</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Connect to high-speed servers worldwide for unrestricted access. Experience the internet without boundaries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden shadow-2xl">
            {/* Left Panel */}
            <div className="relative bg-gradient-to-b from-blue-50 to-white p-12 flex flex-col justify-between min-h-[600px]">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-8">GET IN TOUCH</p>
              </div>
              <div>
                <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                  MOLINHAS<br />LTDA
                </h2>
              </div>
              <div className="absolute inset-0 opacity-5">
                <div className="w-full h-full" style={{ 
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.1) 35px, rgba(0,0,0,.1) 70px)',
                }}></div>
              </div>
            </div>

            {/* Right Panel - Contact Form */}
            <div className="bg-white p-12 flex items-center justify-center">
              <div className="w-full max-w-md">
                <h3 className="text-4xl font-bold text-gray-900 text-center mb-4">Start Your Secure Journey</h3>
                <p className="text-gray-600 text-center mb-12">Contact us for any questions about our VPN service</p>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Name"
                      required
                      className="w-full bg-transparent border-b-2 border-gray-300 py-3 px-0 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Your Email"
                      required
                      className="w-full bg-transparent border-b-2 border-gray-300 py-3 px-0 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Your Phone"
                      className="w-full bg-transparent border-b-2 border-gray-300 py-3 px-0 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Your Message"
                      maxLength={500}
                      rows={4}
                      required
                      className="w-full bg-transparent border-b-2 border-gray-300 py-3 px-0 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gray-900 text-white py-4 rounded-full font-semibold hover:bg-gray-800 transition-colors duration-300 flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12">
            {/* Left Side - Negative Space */}
            <div className="md:col-span-2 bg-gray-50 rounded-3xl p-12 flex items-start">
              <p className="text-xs uppercase tracking-wider text-gray-500">WHO WE ARE</p>
            </div>

            {/* Right Side - Content */}
            <div className="md:col-span-3 flex flex-col justify-center">
              <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-2">
                MOLINHAS LTDA
              </h2>
              <h3 className="text-5xl md:text-6xl font-extrabold text-gray-800 leading-tight mb-12">
                Protecting Digital Lives
              </h3>

              <p className="text-gray-700 text-base leading-relaxed mb-8">
                Based in Belo Horizonte, Brazil, we develop cutting-edge security applications focused on protecting your digital privacy. Our VPN service represents our commitment to providing enterprise-level security accessible to everyone.
              </p>

              <div className="space-y-4 mb-12">
                <div className="flex items-start space-x-3">
                  <i className="ri-map-pin-line text-xl text-blue-500 mt-1"></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Address</p>
                    <p className="text-sm text-gray-600">Rua Padre Paraiso 203, Belo Horizonte, 30710-030, Brazil</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <i className="ri-phone-line text-xl text-blue-500 mt-1"></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">+1 775-295-7866</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <i className="ri-mail-line text-xl text-blue-500 mt-1"></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Email</p>
                    <p className="text-sm text-gray-600">info@molinhasltda.com</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-3xl overflow-hidden shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Abstract%20cybersecurity%20visualization%20with%20digital%20lock%20symbols%20and%20encrypted%20data%20patterns%20in%20blue%20and%20cyan%20colors%2C%20modern%20security%20technology%20concept%20art%20with%20geometric%20shapes%20and%20network%20protection%20elements%2C%20minimalist%20tech%20background%20with%20gradient%20overlay&width=600&height=700&seq=about-img-001&orientation=portrait" 
                    alt="Security Visualization" 
                    className="w-full h-80 object-cover object-top"
                  />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Global%20network%20connectivity%20visualization%20with%20world%20map%20and%20glowing%20connection%20nodes%20in%20blue%20and%20cyan%20colors%2C%20international%20VPN%20server%20network%20concept%20with%20data%20flow%20lines%20across%20continents%2C%20modern%20tech%20illustration%20with%20clean%20minimalist%20design&width=700&height=700&seq=about-img-002&orientation=squarish" 
                    alt="Global Network" 
                    className="w-full h-80 object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/8d07b83a-651c-4150-bc43-cd55f3c060c7.png" 
                  alt="MOLINHAS LTDA" 
                  className="h-10 w-auto brightness-0 invert"
                />
              </div>
              <div className="space-y-2 text-sm text-gray-300">
                <p>Enterprise VPN Solutions</p>
                <p>Privacy-First Approach</p>
                <p>Trusted Security Partner</p>
              </div>
            </div>

            {/* Newsletter Column */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-4">STAY UPDATED</h4>
              <div className="relative mb-3">
                <input
                  type="email"
                  placeholder="Your email"
                  className="w-full bg-transparent border-b border-white/30 py-2 pr-10 text-sm text-white placeholder-gray-400 focus:border-white focus:outline-none"
                />
                <button className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer">
                  <i className="ri-arrow-right-line text-white"></i>
                </button>
              </div>
              <p className="text-xs text-gray-400">
                Get security tips and updates. <a href="#" className="underline hover:text-white">Privacy Policy</a>
              </p>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-4">COMPANY</h4>
              <div className="space-y-3">
                <a href="#about" className="block text-sm text-white hover:opacity-80 transition-opacity cursor-pointer">About Us</a>
                <a href="#contact" className="block text-sm text-white hover:opacity-80 transition-opacity cursor-pointer">Contact</a>
                <a href="#" className="block text-sm text-white hover:opacity-80 transition-opacity cursor-pointer">Privacy Policy</a>
              </div>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-4">REACH US</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-2">
                  <i className="ri-mail-line text-base"></i>
                  <span>info@molinhasltda.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-phone-line text-base"></i>
                  <span>+1 775-295-7866</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-base"></i>
                  <span>Belo Horizonte, BR</span>
                </div>
              </div>
            </div>
          </div>

          {/* Large Brand Typography */}
          <div className="relative py-12 overflow-hidden">
            <h2 className="text-9xl md:text-[180px] font-extrabold text-amber-100/15 tracking-tight leading-none" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-2px' }}>
              MOLINHAS
            </h2>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
              <p>&copy; 2025 MOLINHAS LTDA. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer mt-2 md:mt-0">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
